<template>
  <v-dialog v-model="open" width="592">
    <v-card class="px-2 pb-4">
      <!-- Header -->
      <v-card-title class="d-flex align-center justify-space-between pa-4">
        <span>{{ $t('message.playlist.share.title') }}</span>
        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-col cols="12" class="mb-5 mt-0">
        <v-divider></v-divider>
      </v-col>

      <!-- Warning Message -->
      <v-alert
        v-if="!hasContent"
        class="mx-4"
        dense
        style="
          border-radius: var(--spacing-02, 4px);
          border: 1px solid var(--feedback-warning, #ffc107);
          background: var(--feedback-warning-background, #fff8f0);
        "
      >
        <div class="d-flex align-center">
          <v-icon left color="#FFC107" class="mr-2">mdi-alert</v-icon>
          <span class="ml-1">
            {{ $t('message.playlist.share.warning') }}
            <br />
            {{ $t('message.playlist.share.warningMessage') }}
          </span>
        </div>
      </v-alert>

      <!-- Content -->
      <v-card-text>
        <v-row class="mt-6">
          <v-col cols="6">
            <div class="text-subtitle-2 access-title">
              {{ $t('message.playlist.share.access.title') }}
            </div>
            <div class="text-caption access-description">
              {{ $t('message.playlist.share.access.description') }}
            </div>
          </v-col>
          <v-col cols="6">
            <v-select
              v-model="accessType"
              :items="accessOptions"
              item-text="text"
              item-value="value"
              outlined
              dense
              hide-details
              class="access-select"
            ></v-select>
          </v-col>
        </v-row>

        <v-row v-if="showPasswordField" class="mt-6">
          <v-col cols="6">
            <div class="text-subtitle-2 access-title">
              {{ $t('message.playlist.share.password.title') }}
            </div>
            <div class="text-caption access-description">
              {{ $t('message.playlist.share.password.description') }}
            </div>
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model="password"
              outlined
              dense
              hide-details
              :placeholder="$t('message.playlist.share.password.placeholder')"
              class="access-select"
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Access Link Section -->
        <div class="mt-8">
          <div class="text-subtitle-2 mb-2">
            {{ $t('message.playlist.share.access.link') }}
          </div>
          <v-text-field
            v-model="shareLink"
            readonly
            outlined
            hide-details
            :disabled="!shareLink"
            class="placeholder-text"
            :placeholder="
              hasContent ? '' : $t('message.playlist.share.access.placeholder')
            "
            append-icon="mdi-content-copy"
            @click:append="copyLink"
          ></v-text-field>
        </div>
      </v-card-text>
      <v-card-actions
        :class="isMobileBreakpoint ? 'mobile-actions' : ''"
        class="px-8 pb-6"
      >
        <v-spacer></v-spacer>
        <base-btn
          type="tertiary"
          class="mr-4 px-4 rounded-lg elevation-0"
          :disabled="isFetching"
          color="#EDEEF1"
          @click="closeDialog"
        >
          {{ $t('message.cancel') }}
        </base-btn>
        <base-btn
          type="primary"
          large
          :loading="isFetching"
          :disabled="isFetching"
          @click="handleConfirm"
        >
          {{ $t('message.actions.save') }}
        </base-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import playlistApi from '@/services/playlistApi'
import { mapMutations, mapState } from 'vuex'

export default {
  name: 'PlaylistShareModal',
  data() {
    return {
      open: false,
      accessType: 'anyone_with_link',
      accessOptions: [
        {
          text: this.$t(
            'message.playlist.share.access.options.anyoneWithLink.text'
          ),
          value: 'anyone_with_link',
        },
        {
          text: this.$t(
            'message.playlist.share.access.options.anyoneWithPassword.text'
          ),
          value: 'anyone_with_password',
        },
      ],
      password: '',
      shareLink: '',
      hasContent: false,
      playlist: null,
      isFetching: false,
    }
  },
  mounted() {
    const isStaging = process.env.VUE_APP_ENV !== 'production'
    this.$root.$on('openPlaylistShareModal', (playlist) => {
      this.playlist = playlist
      this.accessType = playlist.access_type
      this.password = playlist.password ? playlist.password : ''
      this.shareLink = isStaging
        ? `https://playlist-staging.tv.pandavideo.com/playlist/${playlist.id}`
        : `https://playlist.tv.pandavideo.com/playlist/${playlist.id}`
      this.openDialog()
    })
  },
  beforeDestroy() {
    this.$root.$off('openPlaylistShareModal')
  },
  computed: {
    ...mapState('user', {
      isMobileBreakpoint: 'isMobileBreakpoint',
    }),
    showPasswordField() {
      return this.accessType === 'anyone_with_password'
    },
  },
  methods: {
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    async handleConfirm() {
      this.isFetching = true
      try {
        await playlistApi.updatePlaylist(
          { access_type: this.accessType, password: this.password },
          this.playlist.id
        )
        this.playlist.access_type = this.accessType
        this.playlist.password = this.password
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          text: this.$t('message.playlist.share.password.success'),
          type: 'success',
        })
        this.$root.$emit('updatePlaylistAccessType', this.playlist.access_type)
      } catch (e) {
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          text: `${e}`,
        })
      }
      this.isFetching = false
      this.closeDialog()
    },
    openDialog() {
      this.open = true
    },
    closeDialog() {
      this.open = false
    },
    copyLink() {
      if (this.shareLink) {
        navigator.clipboard.writeText(this.shareLink)
      }
    },
  },
}
</script>

<style scoped>
.v-card-title {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.access-title {
  color: var(--neutral-20-dark, #282931);
  font-family: Rubik;
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
}

.access-description {
  color: var(--neutral-40-light, #636578);
  font-family: Rubik;
  font-size: 12px;
  font-weight: 400;
  line-height: 150%;
}

.access-select :deep(.v-select__selection) {
  color: var(--neutral-40-light, #636578);
  font-family: Rubik;
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
}

.placeholder-text :deep(.v-text-field__placeholder) {
  color: var(--neutral-70, #abadba);
  font-family: Rubik;
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
}
</style>
